import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useNotification } from '@/hooks/useNotification'
import {
  changePassword,
  setPassword,
  linkEmail,
  setUsername,
  fetchEconomyActivities,
  type EconomyActivity
} from '@/services/userService'
import { fetchUserOrders, type Order } from '@/services/shopService'
import { ApiError } from '@/services/authService'

function ProfilePage() {
  const { user, loading: authLoading, refetchUser } = useAuth()
  const { showNotification } = useNotification()

  const [orders, setOrders] = useState<Order[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [activities, setActivities] = useState<EconomyActivity[]>([])
  const [activitiesLoading, setActivitiesLoading] = useState(false)

  // Form visibility states
  const [showSetUsername, setShowSetUsername] = useState(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showSetPassword, setShowSetPassword] = useState(false)
  const [showLinkEmail, setShowLinkEmail] = useState(false)

  // Form values
  const [usernameInput, setUsernameInput] = useState('')
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [email, setEmail] = useState('')
  const [emailPassword, setEmailPassword] = useState('')

  const [isProcessing, setIsProcessing] = useState(false)

  // Derived state from user
  const hasUsername = !!user?.username
  const hasEmail = !!user?.email
  const hasPassword = !!user?.hasPassword
  const hasDiscord = !!user?.discordId

  useEffect(() => {
    if (!user) return

    let cancelled = false

    const loadOrders = async () => {
      setOrdersLoading(true)
      try {
        const data = await fetchUserOrders(user.id)
        if (!cancelled) {
          setOrders(data)
        }
      } catch (error) {
        console.error('Failed to load orders:', error)
      } finally {
        if (!cancelled) {
          setOrdersLoading(false)
        }
      }
    }

    const loadActivities = async () => {
      setActivitiesLoading(true)
      try {
        const data = await fetchEconomyActivities(user.id)
        if (!cancelled) {
          setActivities(data)
        }
      } catch (error) {
        console.error('Failed to load activities:', error)
      } finally {
        if (!cancelled) {
          setActivitiesLoading(false)
        }
      }
    }

    void loadOrders()
    void loadActivities()

    return () => {
      cancelled = true
    }
  }, [user])

  if (authLoading) {
    return <div className="loading-screen">Načítání profilu…</div>
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: '/profile' }} replace />
  }

  const resetForms = () => {
    setUsernameInput('')
    setOldPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setEmail('')
    setEmailPassword('')
  }

  const handleSetUsername = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!usernameInput || usernameInput.length < 3) {
      showNotification('error', 'Neplatné uživatelské jméno', 'Jméno musí mít alespoň 3 znaky')
      return
    }

    setIsProcessing(true)

    try {
      const response = await setUsername(usernameInput)
      if (response.success) {
        showNotification('success', 'Jméno nastaveno', response.message)
        setShowSetUsername(false)
        resetForms()
        await refetchUser()
      } else {
        showNotification('error', 'Chyba', response.message)
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Jméno se nepodařilo nastavit.'
      showNotification('error', 'Chyba', message)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!oldPassword || !newPassword || !confirmPassword) {
      showNotification('error', 'Neúplné údaje', 'Vyplňte všechna pole')
      return
    }

    if (newPassword !== confirmPassword) {
      showNotification('error', 'Hesla se neshodují', 'Zadejte stejné heslo dvakrát')
      return
    }

    setIsProcessing(true)

    try {
      const response = await changePassword(oldPassword, newPassword)
      if (response.success) {
        showNotification('success', 'Heslo změněno', response.message)
        setShowChangePassword(false)
        resetForms()
      } else {
        showNotification('error', 'Chyba', response.message)
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Heslo se nepodařilo změnit.'
      showNotification('error', 'Chyba', message)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleSetPassword = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!newPassword || !confirmPassword) {
      showNotification('error', 'Neúplné údaje', 'Vyplňte všechna pole')
      return
    }

    if (newPassword !== confirmPassword) {
      showNotification('error', 'Hesla se neshodují', 'Zadejte stejné heslo dvakrát')
      return
    }

    setIsProcessing(true)

    try {
      const response = await setPassword(newPassword)
      if (response.success) {
        showNotification('success', 'Heslo nastaveno', response.message)
        setShowSetPassword(false)
        resetForms()
        await refetchUser()
      } else {
        showNotification('error', 'Chyba', response.message)
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Heslo se nepodařilo nastavit.'
      showNotification('error', 'Chyba', message)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleLinkEmail = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!email || !emailPassword) {
      showNotification('error', 'Neúplné údaje', 'Vyplňte email i heslo')
      return
    }

    setIsProcessing(true)

    try {
      const response = await linkEmail(email, emailPassword)
      if (response.success) {
        showNotification('success', 'Email propojen', response.message)
        setShowLinkEmail(false)
        resetForms()
        await refetchUser()
      } else {
        showNotification('error', 'Chyba', response.message)
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Email se nepodařilo propojit.'
      showNotification('error', 'Chyba', message)
    } finally {
      setIsProcessing(false)
    }
  }

  const displayName = user.username || user.email || 'Uživatel'
  const avatarLetter = (user.username || user.email || 'U').charAt(0).toUpperCase()

  return (
    <div className="profile-page">
      <div className="container">
        {/* Profile Header */}
        <div className="profile-header">
          <div className="user-info">
            <div className="avatar-placeholder">{avatarLetter}</div>
            <div>
              <h1>{displayName}</h1>
              <div className="user-badges">
                {hasDiscord && <span className="badge badge-discord">Discord</span>}
                {hasEmail && <span className="badge badge-email">Email</span>}
              </div>
            </div>
          </div>
          <div className="user-stats">
            <div>
              <span className="stat-label">Mince</span>
              <span className="stat-value">{user.economyStats?.coinsCount?.toLocaleString() ?? '0'}</span>
            </div>
            <div>
              <span className="stat-label">XP</span>
              <span className="stat-value">{user.economyStats?.xpCount?.toLocaleString() ?? '0'}</span>
            </div>
            <div>
              <span className="stat-label">Streak</span>
              <span className="stat-value">{user.economyStats?.dailyStreak ?? '0'}</span>
            </div>
            <button className="btn-secondary" onClick={() => refetchUser()}>
              Aktualizovat data
            </button>
          </div>
        </div>

        {/* Account Setup Alert - shown if incomplete */}
        {(!hasUsername || !hasEmail) && (
          <section className="profile-card profile-alert">
            <h2>Dokončete nastavení účtu</h2>
            <p>Pro plný přístup ke všem funkcím doporučujeme:</p>
            <ul>
              {!hasUsername && <li>Nastavit uživatelské jméno pro komunitu</li>}
              {!hasEmail && <li>Propojit email pro alternativní přihlášení</li>}
            </ul>
          </section>
        )}

        <div className="profile-grid">
          {/* Connected Accounts Section */}
          <section className="profile-card">
            <h2>Propojené účty</h2>
            <div className="account-status-list">
              {/* Discord Status */}
              <div className="account-status-item">
                <div className="account-status-icon discord">
                  <svg width="20" height="20" viewBox="0 0 71 55" fill="currentColor">
                    <path d="M60.1 4.9C55.6 2.8 50.7 1.3 45.7.4c-.1 0-.2 0-.2.1-.6 1.1-1.3 2.6-1.8 3.7-5.5-.8-10.9-.8-16.3 0-.5-1.1-1.2-2.6-1.8-3.7 0-.1-.1-.1-.2-.1-5 .9-9.9 2.4-14.4 4.5 0 0 0 0-.1.1C1.6 18.7-.9 32.1.3 45.4v.2c6.1 4.5 12 7.2 17.7 9 .1 0 .2 0 .3-.1 1.4-1.9 2.6-3.8 3.6-5.9.1-.1 0-.3-.1-.3-1.9-.7-3.8-1.6-5.5-2.6-.1-.1-.1-.3 0-.4.4-.3.8-.6 1.1-.9.1-.1.2-.1.2 0 11.6 5.3 24.2 5.3 35.7 0 .1 0 .2 0 .2 0 .4.3.8.6 1.1.9.1.1.1.3 0 .4-1.8 1-3.6 1.9-5.5 2.6-.1 0-.2.2-.1.3 1 2.1 2.3 4 3.6 5.9.1.1.2.1.3.1 5.7-1.8 11.6-4.5 17.7-9v-.2c1.5-15.3-2.5-28.6-10.5-40.4 0 0 0 0-.1-.1zM23.7 37.3c-3.5 0-6.4-3.2-6.4-7.2s2.8-7.2 6.4-7.2c3.6 0 6.4 3.2 6.4 7.2 0 4-2.8 7.2-6.4 7.2zm23.6 0c-3.5 0-6.4-3.2-6.4-7.2s2.8-7.2 6.4-7.2c3.6 0 6.5 3.2 6.4 7.2 0 4-2.8 7.2-6.4 7.2z"/>
                  </svg>
                </div>
                <div className="account-status-info">
                  <span className="account-status-label">Discord</span>
                  {hasDiscord ? (
                    <span className="account-status-value connected">ID: {user.discordId}</span>
                  ) : (
                    <span className="account-status-value not-connected">Nepropojeno</span>
                  )}
                </div>
              </div>

              {/* Email Status */}
              <div className="account-status-item">
                <div className="account-status-icon email">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <div className="account-status-info">
                  <span className="account-status-label">Email</span>
                  {hasEmail ? (
                    <span className="account-status-value connected">{user.email}</span>
                  ) : (
                    <>
                      <span className="account-status-value not-connected">Nepropojeno</span>
                      <button className="btn-link" onClick={() => setShowLinkEmail(!showLinkEmail)}>
                        Propojit email
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Username Status */}
              <div className="account-status-item">
                <div className="account-status-icon username">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div className="account-status-info">
                  <span className="account-status-label">Uživatelské jméno</span>
                  {hasUsername ? (
                    <span className="account-status-value connected">{user.username}</span>
                  ) : (
                    <>
                      <span className="account-status-value not-connected">Nenastaveno</span>
                      <button className="btn-link" onClick={() => setShowSetUsername(!showSetUsername)}>
                        Nastavit jméno
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Set Username Form */}
            {showSetUsername && !hasUsername && (
              <form className="profile-form" onSubmit={handleSetUsername}>
                <h3>Nastavit uživatelské jméno</h3>
                <p className="form-hint">Jméno bude viditelné v komunitě. Pouze písmena, čísla a podtržítka.</p>
                <div className="form-group">
                  <label htmlFor="username">Uživatelské jméno</label>
                  <input
                    id="username"
                    type="text"
                    value={usernameInput}
                    onChange={(e) => setUsernameInput(e.target.value)}
                    placeholder="moje_jmeno"
                    pattern="^[a-zA-Z0-9_]+$"
                    minLength={3}
                    maxLength={50}
                    required
                  />
                </div>
                <button className="btn btn--primary" type="submit" disabled={isProcessing}>
                  {isProcessing ? 'Ukládám…' : 'Nastavit jméno'}
                </button>
              </form>
            )}

            {/* Link Email Form */}
            {showLinkEmail && !hasEmail && (
              <form className="profile-form" onSubmit={handleLinkEmail}>
                <h3>Propojit email</h3>
                <p className="form-hint">Budete se moci přihlašovat i emailem.</p>
                <div className="form-group">
                  <label htmlFor="emailLink">Email</label>
                  <input
                    id="emailLink"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="emailPassword">Heslo pro přihlášení</label>
                  <input
                    id="emailPassword"
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    minLength={6}
                    required
                  />
                </div>
                <button className="btn btn--primary" type="submit" disabled={isProcessing}>
                  {isProcessing ? 'Propojuji…' : 'Propojit email'}
                </button>
              </form>
            )}
          </section>

          {/* Security Section */}
          <section className="profile-card">
            <h2>Zabezpečení</h2>
            <div className="account-status-list">
              <div className="account-status-item">
                <div className="account-status-icon password">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div className="account-status-info">
                  <span className="account-status-label">Heslo</span>
                  {hasPassword ? (
                    <>
                      <span className="account-status-value connected">Nastaveno</span>
                      <button className="btn-link" onClick={() => setShowChangePassword(!showChangePassword)}>
                        Změnit heslo
                      </button>
                    </>
                  ) : hasEmail ? (
                    <>
                      <span className="account-status-value not-connected">Nenastaveno</span>
                      <button className="btn-link" onClick={() => setShowSetPassword(!showSetPassword)}>
                        Nastavit heslo
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="account-status-value not-connected">Nenastaveno</span>
                      <span className="form-hint">Nejprve propojte email</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Change Password Form */}
            {showChangePassword && hasPassword && (
              <form className="profile-form" onSubmit={handleChangePassword}>
                <h3>Změnit heslo</h3>
                <div className="form-group">
                  <label htmlFor="oldPassword">Současné heslo</label>
                  <input
                    id="oldPassword"
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newPassword">Nové heslo</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Potvrzení nového hesla</label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                  />
                </div>
                <button className="btn btn--primary" type="submit" disabled={isProcessing}>
                  {isProcessing ? 'Ukládám…' : 'Změnit heslo'}
                </button>
              </form>
            )}

            {/* Set Password Form */}
            {showSetPassword && !hasPassword && hasEmail && (
              <form className="profile-form" onSubmit={handleSetPassword}>
                <h3>Nastavit heslo</h3>
                <div className="form-group">
                  <label htmlFor="newPasswordSet">Nové heslo</label>
                  <input
                    id="newPasswordSet"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    minLength={6}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPasswordSet">Potvrzení nového hesla</label>
                  <input
                    id="confirmPasswordSet"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                    required
                  />
                </div>
                <button className="btn btn--primary" type="submit" disabled={isProcessing}>
                  {isProcessing ? 'Ukládám…' : 'Nastavit heslo'}
                </button>
              </form>
            )}
          </section>
        </div>

        {/* Orders Section */}
        <section className="profile-section">
          <h2>Objednávky</h2>
          {ordersLoading ? (
            <p>Načítání objednávek…</p>
          ) : orders.length === 0 ? (
            <p>Zatím nemáte žádné objednávky.</p>
          ) : (
            <div className="orders-grid">
              {orders.map((order) => (
                <div key={order.id} className="order-card">
                  <div className="order-header">
                    <h3>{order.product?.name ?? 'Produkt'}</h3>
                    <span className="order-status">{order.status}</span>
                  </div>
                  <p>{order.product?.description}</p>
                  <div className="order-meta">
                    <span>Velikost: {order.size}</span>
                    <span>Cena: {order.price.toLocaleString()} mincí</span>
                  </div>
                  <div className="order-date">{new Date(order.createdAt).toLocaleDateString('cs-CZ')}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Activities Section */}
        <section className="profile-section">
          <h2>Aktivity</h2>
          {activitiesLoading ? (
            <p>Načítání aktivit…</p>
          ) : activities.length === 0 ? (
            <p>Zatím nemáte zaznamenané žádné aktivity.</p>
          ) : (
            <div className="activities-list">
              {activities.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div>
                    <strong>{activity.activityType}</strong>
                    <span className="activity-date">{new Date(activity.timestamp).toLocaleString('cs-CZ')}</span>
                  </div>
                  <div className="activity-stats">
                    <span>XP: {activity.xpEarned}</span>
                    <span>Mince: {activity.coinsEarned}</span>
                    {activity.streak && <span>Streak: {activity.streak}</span>}
                    {activity.workType && <span>Typ: {activity.workType}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}

export default ProfilePage
